<template>
  <div>
    <h4>Usage limits</h4>
    <h6 class="text-muted mb-2">Plan and Add-on credits will be shown here, usage is reset at the start of each month</h6>

    <b-container class="clear-margin clear-padding">
      <b-row cols="1" cols-md="2" cols-lg="4">
        <b-col>
          <b-card class="card-border" header-tag="header">
            <template v-if="subscription.usage.documentStorageMonthly * 1.2 < subscription.usage.maxStorage" #header>
              <h6 class="mb-0 primary-color">Active</h6>
            </template>
            <template v-else #header>
              <h6 class="mb-0 warning-color"><b>Upgrade your plan to increase storage</b></h6>
            </template>
            <h4>
              Storage Consumed
            </h4>
            <h6 class="text-muted mb-1">
              Storage used by all users
            </h6>
            <b-progress :value="subscription.usage.documentSizeTotal" :max="subscription.usage.maxStorage" class="mb-1"></b-progress>
            <div>
              <b>{{ documentSizeTotalFormatted }}</b> of <b>{{ maxStorageFormatted }}</b> used
            </div>
          </b-card>
        </b-col>
        <b-col>
          <b-card class="card-border" header-tag="header">
            <template v-if="subscription.usage.eSignEnabled" #header>
              <h6 class="mb-0 primary-color">Active</h6>
            </template>
            <template v-else #header>
              <h6 class="mb-0 warning-color"><b>Upgrade your plan to enable</b></h6>
            </template>
            <div :class="!subscription.usage.eSignEnabled ? 'disabled-card' : ''">
              <h4>
                eSignature
              </h4>
              <h6 class="text-muted mb-1">
                Document eSignature requests consumed
              </h6>
              <b-progress :value="subscription.usage.eSignCountMonthly" :max="subscription.usage.eSignMaxCountMonthly" class="mb-1"></b-progress>
              <div>
                <b>{{ subscription.usage.eSignCountMonthly.toLocaleString('en', { useGrouping: true }) }}</b> of
                <b>{{ subscription.usage.eSignMaxCountMonthly.toLocaleString('en', { useGrouping: true }) }}</b> used
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col>
          <b-card class="card-border" header-tag="header">
            <template v-if="subscription.usage.ocrEnabled" #header>
              <h6 class="mb-0 primary-color">Active</h6>
            </template>
            <template v-else #header>
              <h6 class="mb-0"><b>Upgrade your plan to enable</b></h6>
            </template>
            <div :class="!subscription.usage.ocrEnabled ? 'disabled-card' : ''">
              <h4>
                OCR Tokens
              </h4>
              <h6 class="text-muted mb-1">
                OCR tokens consumed
              </h6>
              <b-progress :value="subscription.usage.ocrCountMonthly" :max="subscription.usage.ocrMaxCountMonthly" class="mb-1"></b-progress>
              <div>
                <b>{{ subscription.usage.ocrCountMonthly.toLocaleString('en', { useGrouping: true }) }}</b> of
                <b>{{ subscription.usage.ocrMaxCountMonthly.toLocaleString('en', { useGrouping: true }) }}</b> used
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col>
          <b-card class="card-border" header-tag="header">
            <template v-if="subscription.usage.tokenCountMonthly * 1.2 < subscription.usage.tokensMaxMonthly" #header>
              <h6 class="mb-0 primary-color">Active</h6>
            </template>
            <template v-else #header>
              <h6 class="mb-0 warning-color"><b>Upgrade your plan to increase tokens</b></h6>
            </template>
            <h4>
              AI Tokens
            </h4>
            <h6 class="text-muted mb-1">
              DocumentGPT AI tokens consumed
            </h6>
            <b-progress :value="subscription.usage.tokenCountMonthly" :max="subscription.usage.tokensMaxMonthly" class="mb-1"></b-progress>
            <div>
              <b>{{ subscription.usage.tokenCountMonthly.toLocaleString('en', { useGrouping: true }) }}</b> of
              <b>{{ subscription.usage.tokensMaxMonthly.toLocaleString('en', { useGrouping: true }) }}</b> used
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { BContainer, BRow, BCol, BCard, BProgress } from 'bootstrap-vue'

export default {
  components: { BContainer, BRow, BCol, BCard, BProgress },
  directives: {
    Ripple
  },
  data: function() {
    return {
      skeletonParameters: { type: 'vertical', count: 1 }
    }
  },
  setup() {},
  computed: {
    ...mapState('userProfile', ['profile']),
    ...mapState('billing', ['customerId', 'organisationIds', 'subscription', 'owner', 'users', 'vaults', 'pricingDetails']),
    totalAvailableLicenses() {
      if (this.subscription.quantity - this.users.length < 0) {
        return 0
      }
      return this.subscription.quantity - this.users.length
    },
    price: {
      get() {
        const pricingDetail = this.pricingDetails.find(x => x.id === this.subscription.priceId)

        return `${pricingDetail.currency === 'usd' ? '$' : '£'} ${(pricingDetail.unit_amount / 100) * this.subscription.quantity}`
      }
    },
    documentSizeTotalFormatted: {
      get() {
        const usage = this.formatStorage(this.subscription.usage.documentSizeTotal)
        return `${usage.size} ${usage.unit}`
      }
    },
    maxStorageFormatted: {
      get() {
        const usage = this.formatStorage(this.subscription.usage.maxStorage)
        return `${usage.size} ${usage.unit}`
      }
    }
  },
  watch: {},
  methods: {
    formatStorage: function(value) {
      if (value <= 1073741824) {
        const formattedSize = Math.round((value / 1048576 + Number.EPSILON) * 100) / 100
        return {
          size: formattedSize.toLocaleString('en', { useGrouping: true }),
          unit: 'MB'
        }
      } else {
        const formattedSize = Math.round((value / 1073741824 + Number.EPSILON) * 100) / 100
        return {
          size: formattedSize.toLocaleString('en', { useGrouping: true }),
          unit: 'GB'
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/_variables';

.warning-color {
  color: $red;
}

.circle {
  border-radius: 50%;
  width: 10px;
  height: 10px;
  background: $primary;
}

.card-border {
  box-shadow: none;
  border: 1px solid $text-muted;
  margin-bottom: 1em;
}

.disabled-card {
  opacity: 0.5;
}
</style>
